import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Model } from "survey-core";
import '../../i18n';
import usePageTitle from '../../Hooks/usePageTitle';
import useBodyClass from '../../Hooks/useBodyClass';
import SurveyComponent, { registerAddressFieldsFromCountry } from 'ad-web-ui-library';
import './captureforms.scss';
import Button from '../UI/Buttons/Button';
import RCH from './RHC';
import useResize from '../../Hooks/useResize';
import usePostMessage from '../../Hooks/usePostMessage';
import { APIGet, APIPost } from '../API/APIRequest';
import { GenericErrorBox } from '../UI/ErrorBox/GenericErrorBox';
import { GetADToken } from '../Utilities/TokenManager';
import { useAuth } from '../Authentication/AuthenticationProvider';
import { AuthenticateWithServerCookie } from '../Utilities/ADFECookie';
import { GetCurrentBrand } from '../Configuration/ConfigurationManager';
import { ActivationAccountMessage } from '../Authentication/ActivationAccountMessage/ActivationAccountMessage';
import DOMPurify from 'dompurify';


/**
 * Reads the form identifier from the query string. The canonical param is "surveycode"
 * (inf_SURVEY.SURVEYCODE), matching the legacy V1 /questionnaireform?surveycode=X links.
 * Matching is case-insensitive so ?surveyCode= and ?SurveyCode= both work. Returns null
 * when not present.
 */
const getSurveyCodeParam = (searchParams) => {
    for (const [key, value] of searchParams.entries()) {
        if (key.toLowerCase() === "surveycode" && value) return value;
    }
    return null;
};

/**
 * Reads the post-form return URL from the query string, case-insensitively, accepting the
 * common aliases used across the app and legacy links ("returnUrl", "returnurl", "returl").
 * Without this, a form opened with a differently-cased param (e.g. ?returl= or ?ReturnUrl=)
 * would yield a null returnUrl, so the submit response has no redirect and the form never
 * navigates away after submission. Returns null when none is present.
 */
const getReturnUrlParam = (searchParams) => {
    const aliases = ["returnurl", "returl"];
    for (const [key, value] of searchParams.entries()) {
        if (aliases.includes(key.toLowerCase()) && value) return value;
    }
    return null;
};

const CaptureForms = (props) => {
    const { t } = useTranslation();
    const { environment, friendlyUrl } = useParams();
    const [searchParams] = useSearchParams();

    // URL parameters
    // The form is addressed by ?surveycode= (inf_SURVEY.SURVEYCODE), matching the legacy V1
    // /questionnaireform?surveycode=X links. This value flows into the internal formId state
    // and the getformbyid API, which still use the "formId" name.
    const formIdParam = getSurveyCodeParam(searchParams);
    const returnUrl = getReturnUrlParam(searchParams);
    const campaignCode = searchParams.get("campaigncode") || searchParams.get("campaignCode");
    const isEmbeddedParam = searchParams.get("embedded") === "true";
    const isEmbedded = props?.isEmbedded || (isEmbeddedParam ? { disableRCH: false } : null);

    // State
    const [formId, setFormId] = useState(formIdParam);
    const [formSchema, setFormSchema] = useState(null);
    // Branching rules (PADD-3742) — read from the top level of the getformbyid response
    // (sibling of surveyData) and passed to SurveyComponent so its BranchingEngine can do
    // conditional page navigation. Null for forms without branching (engine stays dormant).
    const [branchingRules, setBranchingRules] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [open, setOpen] = useState(false);
    const [buttonState, setButtonState] = useState("initial");
    const [submitted, setSubmitted] = useState(false);
    const [redirectingToLogin, setRedirectingToLogin] = useState(false);
    // PADD-3916: tracks whether the one-shot cookie re-resolution (return-from-middleware)
    // has been attempted. State (not a ref) so the redirect effect re-evaluates once it flips.
    const [authRetried, setAuthRetried] = useState(false);
    // PADD-3916: tracks whether a cached sessionStorage adtoken has been validated against the
    // live server cookie. false while the check is pending so the form neither renders nor
    // redirects until we know the cached token still corresponds to a real session.
    const [tokenValidated, setTokenValidated] = useState(false);
    // BEFORE-rules gate (Phase 2): null = not yet checked, true = cleared to show the form.
    // While null (or redirecting) the schema fetch is held back so a disqualified user never
    // loads the form. Set to true once the server says showForm, or on a non-blocking error.
    const [beforeRulesChecked, setBeforeRulesChecked] = useState(false);
    const [redirectingByRule, setRedirectingByRule] = useState(false);
    // Bumped once the SurveyJS model is built so the component re-renders and the form
    // (stored in surveyModelRef, a ref that does not itself trigger renders) becomes visible.
    const [modelReady, setModelReady] = useState(false);

    // Authentication state — questionnaire forms require an authenticated + activated user.
    // Auth is handled inside this component (not PrivateRoute) so we can also render the
    // "not activated" message rather than only redirecting.
    const auth = useAuth();
    const adtoken = GetADToken();
    const { isLoading: authLoading, userData, ReloadUserData, ClearUserData } = auth ?? {};
    // PADD-3916: a cached adtoken in sessionStorage can survive a logout performed elsewhere
    // (e.g. in WV / another tab), making the form think the user is still signed in. Until the
    // cached token has been validated against the live server cookie (see effect below), treat
    // the auth state as unresolved so we neither render the form nor redirect prematurely.
    const isAnonymous = !adtoken?.token && (!userData || userData?.anon);
    const isNotActivated = adtoken && adtoken.isactivated !== undefined && adtoken.isactivated === 0;

    // PADD-3685: per-form "Requires activation" flag from schema.formSettings. Admin only sets it
    // true for Q&As / Website Edit Details, so the form-type restriction is enforced at the source.
    const [requiresActivation, setRequiresActivation] = useState(false);

    // Block only when the form requires activation AND the user is unactivated. Anonymous users
    // are still redirected to login elsewhere — this flag relaxes activation, not authentication.
    const blockForActivation = requiresActivation && isNotActivated;

    // Refs
    const surveyModelRef = useRef(null);
    const surveyComponentRef = useRef(null);
    // Guards the one-time captureformmetadata fetch (PADD-3842 loop fix).
    const fetchedMetadataRef = useRef(false);
    // Guards the one-shot cookie re-resolution on the return-from-middleware leg
    // (PADD-3916 login-loop fix). A ref ensures ReloadUserData runs at most once even
    // across re-renders; the authRetried state mirrors it so the redirect effect re-runs.
    const authRetriedRef = useRef(false);
    // Guards the one-time cached-token cookie validation (PADD-3916). A ref ensures the
    // server cookie check runs at most once; tokenValidated state mirrors it for rendering.
    const tokenValidatedRef = useRef(false);

    // Hooks
    const windowSize = useResize();
    const isMobile = windowSize < 1000;

    useBodyClass(['captureforms', props.brandingElement?.bodyClass]);

    // Document title (PADD-3850): "<page title> | <brand>", where the title part is the current
    // page's title (AD_FEV2_GetFormPages already resolves this as ISNULL(WEB_TITLE, PAGE_TITLE)),
    // falling back to the localized "Questionnaire form" label. Derived from pageNumber (updated on
    // every NEXT/PREVIOUS via survey.onCurrentPageChanged), so usePageTitle re-applies it on paging.
    const brandName = props.brandingElement?.id;
    const currentPageTitle = formSchema?.pages?.[pageNumber]?.title;
    const titlePart = currentPageTitle || t('captureform.webtitle');
    usePageTitle(brandName ? `${titlePart} | ${brandName}` : titlePart);

    // PostMessage for embedded mode
    const targetUrl = sessionStorage.getItem("ReturnUrl") || returnUrl;
    usePostMessage({
        targetOrigin: targetUrl,
        containerClass: 'container-wrapper',
        messageType: 'embeddedResize',
    });

    // ---------------------------------------------------------------------
    // Auto-Save removed (PADD-3916). The form no longer writes answers to
    // sessionStorage, because the key was scoped only to formId (not the user):
    // after a logout/login with a different user id, the previous user's draft
    // was restored into the form. On mount we also purge any stale
    // formAutoSave_* keys left behind by earlier builds so they can't leak
    // across users.
    // ---------------------------------------------------------------------
    useEffect(() => {
        try {
            for (let i = sessionStorage.length - 1; i >= 0; i--) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith("formAutoSave_")) {
                    sessionStorage.removeItem(key);
                }
            }
        } catch (e) {
            // sessionStorage unavailable — nothing to clean up.
        }
    }, []);

    // ---------------------------------------------------------------------
    // Auth: validate a cached sessionStorage token against the live server cookie (PADD-3916).
    // The core of the sign-out bug: a stale adtoken left in sessionStorage (e.g. after logging
    // out in WV or another tab) makes the form treat the user as authenticated, because the
    // shared auth provider short-circuits on any cached token without re-checking the cookie.
    // On mount we verify the cookie once:
    //   - cached token + valid cookie  -> keep the session (user really is logged in).
    //   - cached token + no cookie      -> stale token: ClearUserData() wipes it so the user
    //                                      resolves to anonymous and is redirected to login.
    //   - no cached token               -> nothing to validate; the provider already resolved.
    // We skip the check when a ?ref= is present (the return-from-login leg carries its own
    // session), so this never interferes with a fresh login.
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (authLoading) return;                 // wait for the provider to resolve first
        if (tokenValidatedRef.current) return;   // only validate once

        const hasRefParam = searchParams.has("ref");
        const cached = GetADToken();

        // Nothing cached (or arriving from a fresh login): let the provider's result stand.
        if (!cached?.token || hasRefParam) {
            tokenValidatedRef.current = true;
            setTokenValidated(true);
            return;
        }

        let cancelled = false;
        AuthenticateWithServerCookie()
            .then((user) => {
                if (cancelled) return;
                if (!user) {
                    // Cookie gone -> the cached token is stale (signed out elsewhere).
                    if (typeof ClearUserData === "function") ClearUserData();
                }
            })
            .catch(() => {
                // No valid cookie -> treat the cached token as stale.
                if (!cancelled && typeof ClearUserData === "function") ClearUserData();
            })
            .finally(() => {
                if (cancelled) return;
                tokenValidatedRef.current = true;
                setTokenValidated(true);
            });

        return () => { cancelled = true; };
    }, [authLoading, searchParams, ClearUserData]);

    // ---------------------------------------------------------------------
    // Auth: one-shot re-resolution on the return-from-middleware leg (PADD-3916).
    // The middleware hands auth back via a server cookie (not a URL token), and the
    // provider resolves auth only once. If that first read comes back anonymous (e.g.
    // the cookie-derived session had not been saved yet), re-run ReloadUserData once
    // before deciding to redirect — mirroring PrivateRoute — so we don't bounce an
    // already-logged-in user straight back to the login page (the redirect loop).
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (authLoading || !tokenValidated || !isAnonymous || redirectingToLogin) return;
        if (authRetriedRef.current) return;      // only retry once

        authRetriedRef.current = true;
        if (typeof ReloadUserData === "function") {
            ReloadUserData();                    // re-derive auth from the server cookie
        }
        setAuthRetried(true);                    // flip state so the redirect effect re-evaluates
    }, [authLoading, tokenValidated, isAnonymous, redirectingToLogin, ReloadUserData]);

    // ---------------------------------------------------------------------
    // Auth: redirect anonymous users to the middleware login page.
    // Builds {authmiddlewareurl}/{hostname}/login?ReturnUrl={current url} so the
    // user is returned to this exact form (with query params) after signing in.
    // Only redirect after the one-shot re-resolution above has been attempted, so a
    // user who is actually authenticated via cookie is never sent back to login.
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (authLoading || !tokenValidated || !isAnonymous || redirectingToLogin) return;
        if (!authRetried) return;                // wait for the one-shot retry first

        setRedirectingToLogin(true);

        GetCurrentBrand(null)
            .then((brand) => {
                let host = window.location.hostname;
                if (host === 'localhost') {
                    host = 'ad-frontend.abasoft.co.uk';
                }

                const returnTo = window.location.protocol + "//" + window.location.host
                    + window.location.pathname + window.location.search;

                const loginURL = `${brand.authmiddlewareurl}/${host}/login?ReturnUrl=`
                    + encodeURIComponent(returnTo);

                window.location.href = loginURL;
            })
            .catch((error) => {
                console.error("Redirection to middleware login failed:", error);
                setRedirectingToLogin(false);
                setErrors([t('error.unexpected', 'Something went wrong. Please try again.')]);
            });
    }, [authLoading, tokenValidated, isAnonymous, redirectingToLogin, authRetried, t]);

    // ---------------------------------------------------------------------
    // Resolve friendly URL slug to formId (if route param is used)
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (authLoading || !tokenValidated || isAnonymous || blockForActivation || redirectingToLogin) return; // Wait for auth
        if (!friendlyUrl || formId) return; // Skip if no slug or formId already set

        APIGet({
            controller: "forms",
            action: "getformbyfriendlyurl",
            identifier: friendlyUrl,
            environment: environment
        })
            .then((response) => {
                if (response.data?.success && response.data?.formId) {
                    setFormId(response.data.formId.toString());
                } else {
                    setErrors([t('forms.error.noschema', 'Form not found')]);
                    setIsLoading(false);
                }
            })
            .catch((error) => {
                console.error("Failed to resolve friendly URL:", error);
                setErrors([t('forms.error.loadfailed', 'Failed to load form. Please try again.')]);
                setIsLoading(false);
            });
    }, [friendlyUrl, formId, environment, t, authLoading, tokenValidated, isAnonymous, blockForActivation, redirectingToLogin]);

    // ---------------------------------------------------------------------
    // BEFORE-rules gate (Phase 2): evaluate the form's "before" rules for the logged-in
    // party BEFORE loading/rendering the form.
    //   - rule MET (showForm = true)  -> show the form (continue to schema load).
    //   - rule NOT met (showForm=false) -> do NOT show the form; redirect to the retURL
    //     (the returnUrl the user arrived with). If there is no returnUrl, fall back to
    //     showing the form so the user is never stuck.
    // requiresLogin should not normally occur here (anonymous users are already redirected to
    // login above), but is handled defensively. Runs once per formId after auth resolves;
    // holds back the schema fetch until it clears.
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (authLoading || !tokenValidated || isAnonymous || blockForActivation || redirectingToLogin) return; // Wait for auth
        if (!formId) return;              // Need a formId first (friendly-url resolver may still be running)
        if (beforeRulesChecked || redirectingByRule) return; // Already evaluated / redirecting

        let cancelled = false;

        APIGet({
            controller: "form",
            action: "beforerules",
            identifier: formId,
            environment: environment,
            headers: [{ key: "UserToken", value: GetADToken()?.token ?? "" }]
        })
            .then((response) => {
                if (cancelled) return;
                const data = response?.data;

                if (data?.requiresLogin) {
                    // Party could not be resolved server-side (rare — anonymous users are normally
                    // redirected to login before reaching here). Drive the middleware login redirect
                    // so the user isn't stuck on the skeleton, returning to this exact form after login.
                    setRedirectingByRule(true);
                    GetCurrentBrand(null)
                        .then((brand) => {
                            let host = window.location.hostname;
                            if (host === 'localhost') {
                                host = 'ad-frontend.abasoft.co.uk';
                            }
                            const returnTo = window.location.protocol + "//" + window.location.host
                                + window.location.pathname + window.location.search;
                            window.location.href = `${brand.authmiddlewareurl}/${host}/login?ReturnUrl=`
                                + encodeURIComponent(returnTo);
                        })
                        .catch((err) => {
                            console.error("Before-rules requiresLogin redirect failed:", err);
                            setErrors([t('error.unexpected', 'Something went wrong. Please try again.')]);
                        });
                    return;
                }

                if (data?.success && data?.showForm === false) {
                    // BEFORE rule NOT met -> the user does not qualify to see the form; send them
                    // to the retURL they arrived with. If no returnUrl is available, fall through
                    // and show the form rather than leaving the user stranded.
                    if (returnUrl) {
                        setRedirectingByRule(true);
                        window.location.href = returnUrl;
                        return;
                    }
                    // No retURL to go to — show the form.
                    setBeforeRulesChecked(true);
                    return;
                }

                // No matching rule (or none configured): proceed to load the form.
                setBeforeRulesChecked(true);
            })
            .catch((error) => {
                // Don't block the form on a gate failure — log and continue to the schema fetch.
                console.error("Before-rules check failed; continuing to form:", error);
                if (!cancelled) setBeforeRulesChecked(true);
            });

        return () => { cancelled = true; };
    }, [formId, environment, authLoading, tokenValidated, isAnonymous, blockForActivation, redirectingToLogin, beforeRulesChecked, redirectingByRule]);

    // ---------------------------------------------------------------------
    // Fetch form schema from API (Pattern 3: Skeleton loading)
    // PADD-3685: activation is NOT gated here — the schema must load so requiresActivation can be
    // read; the block is applied at render via blockForActivation (false until the schema loads).
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (authLoading || !tokenValidated || isAnonymous || blockForActivation || redirectingToLogin) return; // Wait for auth
        if (!beforeRulesChecked || redirectingByRule) return; // Wait for the before-rules gate
        if (!formId) {
            // Only show error if there's no friendlyUrl either (still resolving)
            if (!friendlyUrl) {
                setErrors([t('forms.error.noformid', 'No form ID provided')]);
                setIsLoading(false);
            }
            return;
        }

        APIGet({
            controller: "forms",
            action: "getformbyid",
            identifier: formId,
            environment: environment
        })
            .then((response) => {
                const schema = response.data?.surveyData;
                if (schema) {
                    setFormSchema(schema);
                    // PADD-3685: capture the per-form "Requires activation" flag so the render
                    // guard below can conditionally block unactivated users. Defaults to false
                    // when the backend omits formSettings (older forms / graceful fallback).
                    setRequiresActivation(schema?.formSettings?.requiresActivation === true);
                    // Branching rules live at the top level of the response, not inside surveyData.
                    setBranchingRules(response.data?.branchingRules || null);
                } else {
                    setErrors([t('forms.error.noschema', 'Form not found')]);
                }
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Failed to load form schema:", error);
                setErrors([t('forms.error.loadfailed', 'Failed to load form. Please try again.')]);
                setIsLoading(false);
            });
    }, [formId, environment, t, authLoading, tokenValidated, isAnonymous, blockForActivation, redirectingToLogin, beforeRulesChecked, redirectingByRule]);

    // ---------------------------------------------------------------------
    // Metadata extraction (PADD-3842): output the brand's "Metadata extraction"
    // (MExtraction) text asset into the document head when the user is logged in.
    // Gated behind the same auth resolution as the schema fetch — by the time this
    // runs the user is authenticated + activated. The processed <script> HTML is lifted
    // up via props.setMetaData so the app-root <DynamicScriptComponent> injects/executes
    // it in <head> (and cleans it up on unmount), reusing the existing checkout mechanism.
    // ---------------------------------------------------------------------
    // Initialize GTM for this page once branding has loaded and the user is authenticated.
    // Kept separate from the metadata fetch so GTM init isn't tied to the metadata response,
    // and re-runs when brandingElement arrives (it may still be null on first mount). The
    // init is guarded + idempotent in App (gtmInitialisedRef), so repeat calls are no-ops.
    // Depend on the specific props used (initGtm / brandingElement), NOT the whole `props`
    // object — the parent passes a new `props` reference every render, so depending on
    // `props` would re-run this effect on every render.
    const initGtm = props.initGtm;
    const brandingElement = props.brandingElement;
    const setMetaData = props.setMetaData;

    useEffect(() => {
        if (authLoading || !tokenValidated || isAnonymous || blockForActivation || redirectingToLogin) return; // Wait for auth
        if (!brandingElement) return; // Wait for branding (gtmId lives on it)
        if (initGtm) initGtm();
    }, [authLoading, tokenValidated, isAnonymous, blockForActivation, redirectingToLogin, brandingElement, initGtm]);

    // Metadata fetch (PADD-3842). This effect calls setMetaData(), which updates parent
    // state → re-renders the parent → passes a NEW `props` reference back down. The original
    // code depended on the whole `props` object, so that re-render retriggered the effect →
    // re-fetch → setMetaData → … an infinite request loop (captureformmetadata fired forever).
    //
    // Two defences, both applied:
    //  1. Depend only on stable-by-value primitives (token string, setMetaData setter, auth
    //     flags, environment) — NOT the `props`/`adtoken` objects which get fresh references.
    //  2. A fetchedMetadataRef guard so the fetch runs at MOST ONCE per mount regardless of
    //     any dependency subtlety — this makes the loop structurally impossible.
    const adToken = adtoken?.token ?? "";
    useEffect(() => {
        if (authLoading || !tokenValidated || isAnonymous || blockForActivation || redirectingToLogin) return; // Wait for auth
        if (!setMetaData) return;
        if (!adToken) return; // No token — nothing to output
        if (fetchedMetadataRef.current) return; // Already fetched this mount — never loop.
        fetchedMetadataRef.current = true;

        APIGet({
            controller: "metadata",
            action: "captureformmetadata",
            environment: environment,
            headers: [{ key: "UserToken", value: adToken }]
        })
            .then((response) => {
                if (response.data?.partymetadata) {
                    setMetaData(response.data);
                }
            })
            .catch((error) => {
                // Metadata is non-blocking — a failure must not prevent the form rendering.
                // Allow a retry on a later dependency change since this attempt failed.
                fetchedMetadataRef.current = false;
                console.error("Capture form metadata load failed:", error);
            });
    }, [authLoading, tokenValidated, isAnonymous, blockForActivation, redirectingToLogin, adToken, setMetaData]);

    // ---------------------------------------------------------------------
    // Create SurveyJS model when schema is loaded
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (formSchema && !surveyModelRef.current) {
            // Register the custom "addressFieldsFromCountry" type BEFORE parsing the schema.
            // SurveyJS only recognises a custom element type during new Model(json) if the
            // type was registered beforehand; otherwise the address marker is dropped at
            // parse time and nothing (not even the country dropdown) renders. This call is
            // idempotent, so the library registering it again later is harmless.
            registerAddressFieldsFromCountry();
            const survey = new Model(formSchema);
            surveyModelRef.current = survey;

            // Multi-page support: all pages defined in the schema are preserved. Navigation
            // (Previous/Next/Submit) is rendered by this component and SurveyJS's own nav is
            // kept hidden by the shared SurveyForm. Single-page forms simply have one page.

            // Keep local pageNumber in sync with the survey's current page so the RHC
            // (right-hand-column) content and the nav buttons reflect the active page.
            setPageNumber(survey.currentPageNo || 0);
            survey.onCurrentPageChanged.add((sender) => {
                setPageNumber(sender.currentPageNo);
            });

            // Load dynamic endpoint data (country dropdowns, company lookups)
            survey.onAfterRenderQuestion.add((sender, options) => {
                const q = options.question;
                const dataEndpoint = q.jsonObj?.dataEndpoint || q.dataEndpoint;

                if (q.getType() === 'dropdown' && dataEndpoint) {
                    const apiProps = {
                        controller: "forms",
                        action: dataEndpoint,
                        identifier: formId,
                        environment: environment,
                        data: ""
                    };

                    // Company lookup requires extra params
                    if (dataEndpoint === "getformcompanies") {
                        apiProps.data = {
                            isDisplayAddrLine1: !!q.jsonObj?.isDisplayAddrLine1,
                            isDisplayPostCode: !!q.jsonObj?.isDisplayPostCode,
                            isDisplayTown: !!q.jsonObj?.isDisplayTown,
                            isDisplayCountry: !!q.jsonObj?.isDisplayCountry,
                        };
                    }

                    APIGet(apiProps)
                        .then((response) => {
                            const data = response.data?.choices;
                            if (!data) return;

                            if (dataEndpoint === "getformcompanies") {
                                q.choices = data.map((item) => {
                                    const addressInfo = [item?.AddressLine1, item?.PostCode, item?.Town, item?.Country].filter(Boolean);
                                    const details = addressInfo.length > 0 ? ` (${addressInfo.join(", ")})` : "";
                                    return { value: item.Name, text: `${item.Name}${details}` };
                                });
                            } else {
                                q.choices = data.map((item) => ({
                                    value: item.name,
                                    text: item.name,
                                }));
                            }
                        })
                        .catch((err) => console.error("Endpoint load error:", err));
                }
            });

            // Force re-render to show the form. surveyModelRef is a ref, so assigning to
            // it does not schedule a render — without this state bump the render body keeps
            // hitting `if (!survey) return null` and the form (and its Submit button) never
            // appears, which is why the form could not be submitted.
            setModelReady(true);
        }
    }, [formSchema]);

    // ---------------------------------------------------------------------
    // Page navigation (multi-page forms)
    // Scrolls to the first error on the current page when validation blocks advancing.
    // ---------------------------------------------------------------------
    const scrollToFirstError = () => {
        // Focus the first errored field. This is now the SINGLE focus mechanism: SurveyJS's
        // own auto-focus-on-error is disabled in SurveyForm (initializeModel) because it ran
        // synchronously inside validate() and set focus BEFORE React rendered the error state,
        // so the follow-up re-render discarded that focus (focus only "worked" on the second
        // click). The error markup (.sd-question--error + the focusable control) is added by a
        // React re-render AFTER validate() returns, so we poll across animation frames until it
        // appears, then focus it. Field-type agnostic (text, radio, checkbox, dropdown, DOB).
        // For the DOB date field only the empty parts carry date-dropdown--error, so target
        // those specifically (focusing the first still-missing Day/Month/Year box). Other
        // field types expose a standard input/textarea/select inside the errored question.
        // When TWO adjacent questions error at once, SurveyJS re-syncs the affected rows'
        // visibleElements through an animation group that fires a SECOND, deferred re-render
        // AFTER we first focus — React remounts the first field's input node and the browser
        // drops focus. (An optional field between the two required ones breaks that back-to-back
        // row re-sync, which is why it worked then.) So we don't focus just once: we re-assert
        // focus across a short window, re-querying the first errored control each frame (its id
        // is stable across the remount) and re-focusing whenever focus has drifted off it. Once
        // focus is on the target and stays there for a couple of frames, we stop.
        const selector = ".sd-question--error input, .sd-question--error textarea, .sd-question--error select:not(.date-dropdown), .sd-question--error select.date-dropdown--error, .sd-question--error .boolean-slider-track";
        let attempts = 0;
        let stableFrames = 0;
        const maxAttempts = 45; // ~0.75s at 60fps — spans the error render + the later remount.
        const pin = () => {
            const target = document.querySelector(selector);
            if (target) {
                if (document.activeElement !== target) {
                    // First time we see it, bring it into view; thereafter just re-pin focus.
                    if (stableFrames === 0) {
                        target.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                    target.focus({ preventScroll: true });
                    stableFrames = 0;
                } else {
                    stableFrames++;
                }
                // Focus has held on the target for a few frames — the remount is done, stop.
                if (stableFrames >= 3) return;
            }
            if (attempts++ < maxAttempts) {
                requestAnimationFrame(pin);
            }
        };
        requestAnimationFrame(pin);
    };

    const handleNext = (e) => {
        e.preventDefault();
        const survey = surveyModelRef.current;
        if (!survey) return;
        setErrors(null);
        // Route NEXT through SurveyForm's imperative handle so the BranchingEngine (PADD-3742)
        // can apply conditional page jumps / form completion. The engine (or the plain linear
        // path when branching is off) validates the current page itself and returns false if
        // it was blocked — in which case we scroll to the first error. Calling survey.nextPage()
        // directly here would bypass the engine entirely (this was the original branching bug).
        const moved = surveyComponentRef.current?.nextPage
            ? surveyComponentRef.current.nextPage()
            : (survey.validateCurrentPage() ? (survey.nextPage(), true) : false);
        if (!moved) {
            // PADD-3866: flag the current step in the (in-survey) indicator's error state.
            surveyComponentRef.current?.setErrorStep?.(survey.currentPageNo);
            scrollToFirstError();
        } else {
            // Advanced successfully — clear any prior error flag.
            surveyComponentRef.current?.setErrorStep?.(null);
        }
    };

    const handlePrev = (e) => {
        e.preventDefault();
        const survey = surveyModelRef.current;
        if (!survey) return;
        // Going back never validates — the user may want to revisit earlier answers. Route
        // through the ref so branching's history-based back navigation is used when active.
        setErrors(null);
        if (surveyComponentRef.current?.prevPage) {
            surveyComponentRef.current.prevPage();
        } else {
            survey.prevPage();
        }
    };

    // ---------------------------------------------------------------------
    // Form Submission (Pattern 1: Inline error + retry)
    // ---------------------------------------------------------------------
    const handleSubmit = (e) => {
        e.preventDefault();
        const survey = surveyModelRef.current;
        if (!survey) return;

        // Validate entire form
        const isValid = survey.validate(true);
        if (!isValid) {
            scrollToFirstError();
            return;
        }

        setButtonState("loading");
        setErrors(null);

        // Prefix answers (answer codes for backward compatibility)
        if (surveyComponentRef.current?.prefixAnswers) {
            surveyComponentRef.current.prefixAnswers();
        }

        // Collect form data
        const surveyData = surveyComponentRef.current?.getSurveyData
            ? surveyComponentRef.current.getSurveyData()
            : survey.data;

        // Build submission payload
        const formData = {};
        for (let key in surveyData) {
            const value = surveyData[key];
            if (Array.isArray(value)) {
                formData[key] = value.join(",");
            } else if (value !== null && value !== undefined) {
                formData[key] = String(value);
            }
        }

        const submitPayload = {
            formId: parseInt(formId),
            surveyCode: formSchema?.formSettings?.surveyCode || formId,
            formData: formData,
            returnUrl: returnUrl,
            thankYouPageUrl: formSchema?.formSettings?.thankYouPageUrl || null,
            campaignCode: campaignCode || null
        };

        APIPost({
            controller: "forms",
            action: "submit",
            environment: environment,
            data: submitPayload,
            content_type: "json",
            headers: [{ key: "UserToken", value: GetADToken()?.token ?? "" }]
        })
            .then((response) => {
                const result = response.data;
                if (result.success) {
                    // Handle redirect. A rule/return redirect is a hard navigation, so we navigate
                    // regardless of embedded mode. In embedded mode we also notify the parent (so it
                    // can react), then drive the navigation on the top window if available, falling
                    // back to this window — otherwise an embedded form would never leave the page.
                    //
                    // When a redirect URL is present we do NOT flip `submitted` to true: doing so
                    // would render the thank-you component for the brief moment before the browser
                    // leaves the page, causing a visible flash. We navigate straight away instead,
                    // and only show the thank-you component when there is no redirect to follow.
                    const redirectUrl = result.redirectUrl;
                    if (redirectUrl) {
                        // Defer the hard navigation to the next macrotask. Assigning
                        // window.location.href synchronously inside the same tick that the
                        // fetch/axios response resolves can, in some browsers, tear the page
                        // down before the response (and its underlying connection) is fully
                        // settled. The server-side Inform commit is already awaited before this
                        // success response is sent, but deferring by a tick guarantees the
                        // response is fully consumed on the client before we leave the page —
                        // removing any teardown race on the redirect path.
                        const navigate = () => {
                            if (isEmbedded) {
                                window.parent.postMessage(
                                    { type: "formComplete", success: true, redirectUrl },
                                    targetUrl || "*"
                                );
                                try {
                                    window.top.location.href = redirectUrl;
                                } catch (e) {
                                    // Cross-origin top access blocked — navigate this frame instead.
                                    window.location.href = redirectUrl;
                                }
                            } else {
                                window.location.href = redirectUrl;
                            }
                        };
                        setTimeout(navigate, 0);
                    } else {
                        // No redirect — show the thank-you component.
                        setSubmitted(true);
                        if (isEmbedded) {
                            window.parent.postMessage(
                                { type: "formComplete", success: true },
                                targetUrl || "*"
                            );
                        }
                    }
                } else {
                    setErrors(result.errors || [t('forms.error.submitfailed', 'Submission failed. Please try again.')]);
                    setButtonState("initial");
                }
            })
            .catch((error) => {
                console.error("Form submission failed:", error);
                setErrors([t('forms.error.submitfailed', 'Submission failed. Please try again.')]);
                setButtonState("initial");
            });
    };

    // ---------------------------------------------------------------------
    // Retry handler for schema load failure
    // ---------------------------------------------------------------------
    const handleRetry = () => {
        setErrors(null);
        setIsLoading(true);
        setFormSchema(null);
        setBranchingRules(null);
        surveyModelRef.current = null;
        setModelReady(false);

        APIGet({
            controller: "forms",
            action: "getformbyid",
            identifier: formId,
            environment: environment
        })
            .then((response) => {
                const schema = response.data?.surveyData;
                if (schema) {
                    setFormSchema(schema);
                    setBranchingRules(response.data?.branchingRules || null);
                } else {
                    setErrors([t('forms.error.noschema', 'Form not found')]);
                }
                setIsLoading(false);
            })
            .catch(() => {
                setErrors([t('forms.error.loadfailed', 'Failed to load form. Please try again.')]);
                setIsLoading(false);
            });
    };

    // ---------------------------------------------------------------------
    // Render: Auth resolving or redirecting to login (State 1)
    // Show the skeleton while auth resolves or while we redirect anonymous
    // users to the middleware login page.
    // ---------------------------------------------------------------------
    if (authLoading || !tokenValidated || isAnonymous || redirectingToLogin || redirectingByRule) {
        return (
            <div className="container-wrapper" data-testid="captureforms-auth-loading">
                <section>
                    <div className="container">
                        <div className="item-container">
                            <div className="item-wrapper">
                                <div className="skeleton-block skeleton-title"></div>
                                <div className="skeleton-block skeleton-field"></div>
                                <div className="skeleton-block skeleton-field"></div>
                                <div className="skeleton-block skeleton-field"></div>
                                <div className="skeleton-block skeleton-button"></div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // ---------------------------------------------------------------------
    // Render: User not activated (State 2) — PADD-3685: block ONLY when the form requires
    // activation. Flag-off forms fall through and render; anonymous users redirected earlier.
    // ---------------------------------------------------------------------
    if (blockForActivation) {
        // Resend the activation email for the logged-in-but-unactivated user. No returnUrl is sent,
        // so the backend falls back to the default AD Frontend activation page.
        const handleSendVerification = () => {
            return APIPost({
                controller: "auth",
                action: "sendactivationlink",
                environment: environment,
                headers: [{ key: "UserToken", value: GetADToken()?.token ?? "" }]
            }).then((response) => {
                if (!response?.data?.success) {
                    throw new Error("sendactivationlink failed");
                }
                return response;
            });
        };

        return (
                  <section>
                    <ActivationAccountMessage
                        brandingElement={props.brandingElement}
                        returnUrl={returnUrl}
                        onSendVerification={handleSendVerification}
                    />
                </section>
                   );
    }

    // ---------------------------------------------------------------------
    // Render: Loading state (Pattern 3: Skeleton)
    // ---------------------------------------------------------------------
    if (isLoading) {
        return (
            <div className="container-wrapper" data-testid="captureforms-loading">
                <section>
                    <div className="container">
                        <div className="item-container">
                            <div className="item-wrapper">
                                <div className="skeleton-block skeleton-title"></div>
                                <div className="skeleton-block skeleton-field"></div>
                                <div className="skeleton-block skeleton-field"></div>
                                <div className="skeleton-block skeleton-field"></div>
                                <div className="skeleton-block skeleton-button"></div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // ---------------------------------------------------------------------
    // Render: Error state (no form loaded)
    // ---------------------------------------------------------------------
    if (!formSchema && errors) {
        return (
            <div className="container-wrapper" data-testid="captureforms-error">
                <section>
                    <div className="container">
                        <div className="item-container">
                            <div className="item-wrapper">
                                <GenericErrorBox errors={errors} />
                                <Button onClick={handleRetry} buttonSize="medium">
                                    {t('forms.retry', 'Try Again')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // ---------------------------------------------------------------------
    // Render: Success state (form submitted)
    // ---------------------------------------------------------------------
    if (submitted) {
        // "Forms - Thank you" site-branding text asset (getbranding → captureformThankyou).
        // When the brand has configured it, render that HTML (sanitised) in place of the default
        // localized success line. Falls back to the translated string when the asset is empty.
        const thankYouHtml = props.brandingElement?.captureformThankyou;
        return (
            <div className="container-wrapper two-columns" data-testid="captureforms-success">
                <section>
                    <div className="container">
                        <div className="item-container">
                            <div className="item-wrapper">
                                {thankYouHtml
                                    ? <div className="forms-thankyou" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(thankYouHtml) }} />
                                    : <p>{t('forms.success', 'Thank you. Your form has been submitted successfully.')}</p>
                                }
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // modelReady is included so this line is re-evaluated after the model-building effect
    // runs (the ref assignment alone would not re-render the component).
    const survey = modelReady ? surveyModelRef.current : null;
    if (!survey) return null;

    // Determine whether the RHC (right-hand column) has any content to show for the
    // current page. Mirrors the logic in the RHC component so we don't render an empty
    // <section> wrapper (which would otherwise leave a blank column on the page).
    const rhcPages = formSchema?.pages;
    const hasRhcContent = typeof pageNumber === 'number'
        ? !!rhcPages?.[pageNumber]?.htmlRHC
        : !!rhcPages?.some(p => p?.htmlRHC);

    // ---------------------------------------------------------------------
    // Render: Form
    // ---------------------------------------------------------------------
    return (
        <div className="container-wrapper two-columns" data-testid="captureforms-form">

            {/* Mobile RHC toggle (Pattern 6: aria-expanded) */}
            {!isEmbedded?.disableRCH && isMobile && hasRhcContent && (
                <section>
                    <div className="container">
                        <div className="item-container">
                            <div className="item-wrapper">
                                <div className="two-columns-row">
                                    <button
                                        onClick={() => setOpen(!open)}
                                        className="text-button"
                                        aria-expanded={open}
                                        aria-controls="rhc-expandable"
                                    >
                                        {open
                                            ? t('forms.hideinfo', 'Hide information')
                                            : t('forms.showinfo', 'View more information')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        id="rhc-expandable"
                        className={`expandable ${open ? "open" : ""}`}
                        aria-hidden={!open}
                    >
                        <div>
                            <RCH surveyJson={formSchema} pageNo={pageNumber} />
                        </div>
                    </div>
                </section>
            )}

            {/* Form Section */}
            <section>
                <div className="container">
                    {/* Step indicator (PADD-3866): rendered INSIDE the SurveyJS form (one
                        node per page, injected by the shared SurveyForm after each page
                        title). Hidden automatically for single-page and branching forms, so
                        there is nothing to render here at the host level. */}

                    {/* Inline error (Pattern 1) */}
                    {errors && (
                        <div className="item-container" aria-live="polite">
                            <div className="item-wrapper">
                                <GenericErrorBox errors={errors} />
                            </div>
                        </div>
                    )}

                    {/* SurveyJS Form (using shared library) */}
                    <div className="item-container">
                        <div className="item-wrapper">
                            <SurveyComponent
                                ref={surveyComponentRef}
                                survey={survey}
                                formId={formId}
                                apiGet={APIGet}
                                controller="forms"
                                environment={environment}
                                userToken={GetADToken()?.token ?? ""}
                                prefillLatestAddress={true}
                                enablePaging={true}
                                branchingRules={branchingRules}
                                onPageChanged={(newPageNo) => setPageNumber(newPageNo)}
                                onComplete={(data) => {
                                    // SurveyJS internal completion - not used, we handle submit manually
                                }}
                            />
                        </div>
                    </div>

                    {/* Navigation: Previous / Next / Submit
                        Single-page forms: just Submit (no nav buttons).
                        Multi-page forms:
                          Page 1:      NEXT only
                          Middle:      PREVIOUS + NEXT
                          Last page:   PREVIOUS + SUBMIT
                        Mirrors the old AD's PREVIOUS/NEXT/SUBMIT button pattern. */}
                    <div className="item-container">
                        <div className="item-wrapper one-row form-navigation">
                            {/* PREVIOUS — shown on all pages except the first */}
                            {survey.pageCount > 1 && pageNumber > 0 && (
                                <button
                                    type="button"
                                    className="secondary-button large"
                                    onClick={handlePrev}
                                    data-testid="captureforms-prev-button"
                                >
                                    {t('forms.previous', 'Previous')}
                                </button>
                            )}

                            {/* NEXT — shown on all pages except the last (multi-page only) */}
                            {survey.pageCount > 1 && pageNumber < survey.pageCount - 1 && (
                                <button
                                    type="button"
                                    className="primary-button large"
                                    onClick={handleNext}
                                    data-testid="captureforms-next-button"
                                >
                                    {t('forms.next', 'Next')}
                                </button>
                            )}

                            {/* SUBMIT — shown on the last page (or the only page for single-page
                                forms). Uses the shared Button UI component so it matches the
                                registration pager: while submitting it shows the "updating" loading
                                icon and is disabled, exactly like the registration Register button. */}
                            {(survey.pageCount <= 1 || pageNumber === survey.pageCount - 1) && (
                                buttonState === "loading" ? (
                                    <Button
                                        isDisabled="true"
                                        hasIcon="updating"
                                        buttonSize="full-width"
                                        data-testid="captureforms-submit-button"
                                    >
                                        {t('forms.submit', 'Submit')}
                                    </Button>
                                ) : (
                                    <Button
                                        buttonSize="full-width"
                                        onClick={handleSubmit}
                                        data-testid="captureforms-submit-button"
                                    >
                                        {t('forms.submit', 'Submit')}
                                    </Button>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop RHC (side column) */}
            {!isEmbedded?.disableRCH && !isMobile && hasRhcContent && (
                <section>
                    <RCH surveyJson={formSchema} pageNo={pageNumber} />
                </section>
            )}
        </div>
    );
};

export default CaptureForms;
